// Wygląd — the player's skin, eyes, hairstyle and hair colour (character look, 2026-10-29). The Garderoba's look
// (gameUi.js): a cream panel under a wooden title, the four tabs across the top (Rynek pattern), the character in
// the middle in the current pick, turned round with a slider under her and walking on the spot at a toggle, one big
// row of choices under it, Gotowe. A local build (vite dev) adds tools beside her: the walk held still and stepped a
// frame at a time, a random look, and the exact pose copied for a report. Used by the
// registration (before the account exists) and by Ustawienia (the body is fixed by then: no body choice here).
//
//   openLookPanel(scene, { character, look, equipped, onDone(look), onCancel })
// onDone gets the pick as the server stores it (lookPanelLogic doneLook: only what differs from the body, or null);
// it may return a promise: the panel closes when it resolves and stays open (for another try) when it rejects.
import { GAME_WIDTH, GAME_HEIGHT } from '../config.js';
import { NatalkaSprite } from './NatalkaSprite.js';
import { lockInput, unlockInput } from './inputLock.js';
import { enlargeForTouch, canvasScale } from './touchEnlarge.js';
import { isTouchDevice } from './isTouchDevice.js';
import { trackPanel } from './panelSwitch.js';
import { iconText } from './iconText.js';
import { gamePanel, woodTitle, gameButton, gameCard, cardFeedback, pageArrows } from './gameUi.js';
import { PALETTE, FONT } from './gameUiStyle.mjs';
import { WALK } from './walkAnim.js';
import { LOOK_TABS, fieldOf, optionsFor, swatchesPerRow, pick, doneLook, tileRow, pageOf, facingAt, snapTurn,
  randomLook, stepFrame, poseRecord } from './lookPanelLogic.mjs';
import { resolveLook } from '../look/lookCatalog.mjs';

const W = 760, H = 620;
const TURN_W = 300;             // the turn slider: its width, and how far below the panel's middle it runs (between
const TURN_Y = 60;              // her feet and the row of choices)
const SIDE_X = 250;             // the toggle left of her, the local tools right of her: this far from the middle
const LOCAL_TOOLS = import.meta.env.DEV;

export function openLookPanel(scene, { character = 'natalka', look = null, equipped = null, onDone = () => {}, onCancel = () => {} } = {}) {
  const cam = scene.cameras.main;
  const cx = cam.scrollX + GAME_WIDTH / 2, cy = cam.scrollY + GAME_HEIGHT / 2;
  const layer = scene.add.container(0, 0).setDepth(2100);
  lockInput(scene);
  let current = resolveLook(character, look), tab = LOOK_TABS[0], busy = false, closed = false, turning = false;
  let tilePage = null;            // the hairstyle page shown; null: the one that holds the current style

  const uncover = scene._roomChat?.coverFor?.() ?? (() => {});
  let untrack = () => {};
  const close = () => {
    if (closed) return;
    closed = true; untrack(); stopTurn(); stopTools(); unlockInput(scene); layer.destroy(); uncover();
  };
  const cancel = () => { if (!busy) { close(); onCancel(); } };
  untrack = trackPanel(scene, 'look', cancel);
  scene.events.once('shutdown', close);

  const overlay = scene.add.rectangle(cx - GAME_WIDTH / 2, cy - GAME_HEIGHT / 2, GAME_WIDTH, GAME_HEIGHT, 0x000000, 0.6)
    .setOrigin(0, 0).setInteractive();
  overlay.on('pointerup', () => { if (!turning) cancel(); });   // a slide let go off the panel is no tap
  const panelHit = scene.add.rectangle(cx, cy, W, H, 0xffffff, 0.001).setInteractive();   // a tap on the panel stays here
  layer.add([overlay, panelHit, gamePanel(scene, cx, cy, W, H), woodTitle(scene, cx, cy - H / 2 + 4, '{sparkle} Wygląd', { fontSize: 24 })]);

  // Tabs across the top, the open one in wood.
  const tabY = cy - H / 2 + 64, TAB_W = 170;
  const tabs = LOOK_TABS.map((t, i) => {
    const b = gameButton(scene, cx + (i - (LOOK_TABS.length - 1) / 2) * (TAB_W + 8), tabY, TAB_W, 42, t,
      () => { tab = t; tilePage = null; render(); }, { variant: 'secondary', fontSize: 17 });
    layer.add(b);
    return { t, b };
  });

  // The character in the current pick, a display figure (local: false), facing the camera until turned.
  const preview = new NatalkaSprite(scene, cx, cy - 74, { trackIdle: false, noTweakHud: true, local: false, character, look: current });
  preview.setOutfit(equipped);   // in what they wear (nothing yet at registration)
  preview.setScale(1);
  layer.add(preview);
  preview.setFacing8(facingAt(0.5));

  // The slider that turns her (owner 2026-11-01, instead of a slow spin): press anywhere along it and slide, a stop a
  // compass step; let go, it settles on the nearest. A finger's place is read in the layer's own space (enlargeForTouch
  // scales it on phones).
  const turnL = cx - TURN_W / 2, turnY = cy + TURN_Y;
  let turnT = 0.5;
  const track = scene.add.graphics().fillStyle(PALETTE.secondary.top).fillRoundedRect(turnL - 6, turnY - 5, TURN_W + 12, 10, 5)
    .lineStyle(2, PALETTE.panel.border).strokeRoundedRect(turnL - 6, turnY - 5, TURN_W + 12, 10, 5);
  const stops = [0, 1, 2, 3, 4, 5, 6, 7, 8].map((k) => scene.add.circle(turnL + (k / 8) * TURN_W, turnY, 2, PALETTE.panel.border));
  // finger-sized: it reaches up over her feet (nothing there takes a tap); the choices below sit on top of it
  const turnHit = scene.add.rectangle(cx, turnY - 8, TURN_W + 40, 64, 0xffffff, 0.001).setInteractive({ useHandCursor: true });
  const knob = scene.add.circle(cx, turnY, 13, PALETTE.wood.fill).setStrokeStyle(3, PALETTE.wood.dark);
  layer.add([track, ...stops, turnHit, knob]);
  const turnTo = (t) => { turnT = Math.min(1, Math.max(0, t)); knob.x = turnL + turnT * TURN_W; preview.setFacing8(facingAt(turnT)); };
  const slideTo = (p) => turnTo((layer.pointToContainer({ x: p.worldX, y: p.worldY }).x - turnL) / TURN_W);
  const onMove = (p) => { if (turning && p.isDown) slideTo(p); };
  const onUp = () => { if (turning) { turning = false; turnTo(snapTurn(turnT)); } };
  turnHit.on('pointerdown', (p) => { turning = true; slideTo(p); });
  scene.input.on('pointermove', onMove);
  scene.input.on('pointerup', onUp);
  scene.input.on('pointerupoutside', onUp);
  function stopTurn() {
    scene.input.off('pointermove', onMove); scene.input.off('pointerup', onUp); scene.input.off('pointerupoutside', onUp);
  }

  // Walking on the spot, left of her (owner 2026-11-01); `held`: the walk frame the local stepper holds still.
  let walking = false, held = null;
  const walkBtn = gameButton(scene, cx - SIDE_X, cy - 74, 170, 44, 'Chodzenie', () => {
    walking = !walking; held = null;
    preview.holdWalkFrame(null); preview.walkInPlace(walking); paintTools();
  }, { variant: 'secondary', fontSize: 17 });
  layer.add(walkBtn);
  let stopTools = () => {}, paintTools = () => walkBtn.setVariant(walking ? 'wood' : 'secondary');
  if (LOCAL_TOOLS) localTools();

  // Local only, right of her: ‹ Zatrzymaj › (hold the walk, a frame back / on), the frame shown, Losuj, Kopiuj.
  function localTools() {
    const x = cx + SIDE_X, sub = { variant: 'secondary', fontSize: 15 };
    const note = iconText(scene, x, cy - 196, 'tylko lokalnie', { fontFamily: FONT, fontSize: '13px', color: '#9a7a52' }).setOrigin(0.5);
    const step = (by) => { if (held == null) return; held = stepFrame(held, by); preview.holdWalkFrame(held); paintTools(); };
    const back = gameButton(scene, x - 84, cy - 160, 48, 42, '‹', () => step(-1), sub);
    const on = gameButton(scene, x + 84, cy - 160, 48, 42, '›', () => step(1), sub);
    const hold = gameButton(scene, x, cy - 160, 108, 42, 'Zatrzymaj', () => {
      if (held == null) {
        const shown = preview.shownFrame;
        held = shown?.pose === 'walk' ? shown.col : WALK.startFrame;    // standing: the walk's first frame
        walking = true; preview.walkInPlace(true);
      } else held = null;
      preview.holdWalkFrame(held); paintTools();
    }, sub);
    const frameText = iconText(scene, x, cy - 122, '', { fontFamily: FONT, fontSize: '14px', color: PALETTE.secondary.text }).setOrigin(0.5);
    const random = gameButton(scene, x, cy - 74, 216, 42, 'Losuj', () => {
      tilePage = null; setLook(resolveLook(character, randomLook(character)));
    }, sub);
    let copied = null;
    const copy = gameButton(scene, x, cy - 22, 216, 42, 'Kopiuj', () => {
      const text = poseRecord({ character, look: current, equipped, facing8: preview.facing8, shown: preview.shownFrame });
      window.__lookPose = text;
      console.log('[Wygląd]', text);
      const say = (s) => { copy.setLabel(s); clearTimeout(copied); copied = setTimeout(() => { if (!closed) copy.setLabel('Kopiuj'); }, 1500); };
      if (navigator.clipboard?.writeText) navigator.clipboard.writeText(text).then(() => say('Skopiowano {check}'), () => say('W konsoli'));
      else say('W konsoli');
    }, sub);
    layer.add([note, back, hold, on, frameText, random, copy]);
    const showFrame = () => {
      const f = preview.shownFrame;
      const s = f ? `${f.row}${f.flip ? ' (odbite)' : ''} · klatka ${f.frame}` : '';
      if (frameText.text !== s) frameText.setText(s);
    };
    scene.events.on('update', showFrame);
    stopTools = () => { scene.events.off('update', showFrame); clearTimeout(copied); };
    const paintWalk = paintTools;
    paintTools = () => {
      paintWalk();
      hold.setLabel(held == null ? 'Zatrzymaj' : 'Wznów');
      back.setEnabled(held != null); on.setEnabled(held != null);
    };
  }

  // The choices of the open tab: colour swatches, or hairstyle tiles drawn in the current hair colour.
  const choices = scene.add.container(0, 0);
  layer.add(choices);
  const perRow = swatchesPerRow({ width: GAME_WIDTH * canvasScale(scene), touch: isTouchDevice() });
  const rowsTop = cy + 96;

  const cancelBtn = gameButton(scene, cx - 130, cy + H / 2 - 42, 200, 48, 'Anuluj', cancel, { variant: 'secondary', fontSize: 18 });
  const doneBtn = gameButton(scene, cx + 130, cy + H / 2 - 42, 200, 48, 'Gotowe {check}', async () => {
    if (busy) return;
    busy = true; doneBtn.setEnabled(false);
    try { await onDone(doneLook(character, current)); close(); }
    catch { busy = false; if (!closed) doneBtn.setEnabled(true); }
  }, { variant: 'wood', fontSize: 18 });
  layer.add([cancelBtn, doneBtn]);

  function choose(id) {
    setLook(resolveLook(character, pick(current, tab, id)));
  }

  function setLook(look) {
    current = look;
    preview.setLook(current);
    render();
  }

  function render() {
    for (const { t, b } of tabs) b.setVariant(t === tab ? 'wood' : 'secondary');
    choices.removeAll(true);
    const opts = optionsFor(tab, character), field = fieldOf(tab);
    const size = perRow <= 4 ? 64 : 52;
    if (field === 'hairstyle') { renderTiles(opts, size); return; }
    const n = Math.min(perRow, opts.length), step = Math.min(96, (W - 80) / Math.max(1, n));
    opts.forEach((o, i) => {
      const col = i % perRow, row = Math.floor(i / perRow);
      const inRow = Math.min(perRow, opts.length - row * perRow);
      const x = cx + (col - (inRow - 1) / 2) * step, y = rowsTop + size / 2 + row * (size + 14);
      const selected = current[field] === o.id;
      const ring = scene.add.circle(x, y, size / 2 + 5, selected ? PALETTE.primary.face : 0xffffff, selected ? 1 : 0.001)
        .setStrokeStyle(selected ? 0 : 3, PALETTE.panel.border);
      const dot = scene.add.circle(x, y, size / 2 - 2, Number.parseInt(o.colour.slice(1), 16));
      ring.setInteractive({ useHandCursor: true }).on('pointerup', () => choose(o.id));
      choices.add([ring, dot]);
    });
  }

  // The hairstyle tiles, in the current hair colour: one row, paged with ‹ › when they do not all fit.
  function renderTiles(opts, size) {
    const row = tileRow({ count: opts.length, cardW: size + 40, innerW: W - 80 });
    const page = Math.min(tilePage ?? pageOf(opts.map((o) => o.id), current.hairstyle, row.perPage), row.pages - 1);
    const shown = opts.slice(page * row.perPage, (page + 1) * row.perPage), y = rowsTop + size / 2;
    shown.forEach((o, i) => {
      const x = cx + (i - (shown.length - 1) / 2) * row.step;
      const selected = current.hairstyle === o.id;
      const card = gameCard(scene, x, y + 20, size + 40, size + 70, { selected });
      const mini = new NatalkaSprite(scene, x, y + 22, { trackIdle: false, noTweakHud: true, local: false, character,
        look: { ...current, hairstyle: o.id } });
      mini.setOutfit(equipped);
      mini.setFacing8(2);
      mini.setScale(0.32);
      cardFeedback(scene, card, [mini], selected ? null : () => choose(o.id));   // moves the figure into the card
      choices.add(card);
    });
    if (row.arrows) choices.add(pageArrows(scene, cx, y + 20, row.halfSpan, page, row.pages, (p) => { tilePage = p; render(); }));
  }

  enlargeForTouch(scene, layer, { x: cx, y: cy, w: W, h: H }, { overlay });   // finger-sized on phones
  paintTools();
  render();
  return close;
}
